import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { toast } from 'react-toastify';
import { atlasClient } from '../../../../api/atlasClient';
import { ImportPreviewResponse } from '../../types/importTypes';
import { ImportPreviewModal } from './ImportPreviewModal';
import boardStyles from '../../styles/CreativeBoard.module.css';

interface ImportDropdownProps {
  creativeTypeFqn: string | null;
  creativeTypeName: string;
  onImportComplete: () => void;
  disabled: boolean;
  buttonClassName?: string;
}

export const ImportDropdown: React.FC<ImportDropdownProps> = ({
  creativeTypeFqn,
  creativeTypeName,
  onImportComplete,
  disabled,
  buttonClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<ImportPreviewResponse | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // PDF import state
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [isPdfUploading, setIsPdfUploading] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDownloadTemplate = () => {
    if (!creativeTypeFqn) return;
    const url = atlasClient.getLibraryImportTemplateUrl(creativeTypeFqn);
    window.open(url, '_blank');
    setIsOpen(false);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset to allow re-selecting same file
      fileInputRef.current.click();
    }
    setIsOpen(false);
  };

  const handleFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !creativeTypeFqn) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setIsUploading(true);
    const uploadToast = toast.loading('Processing file...');

    try {
      const response = await atlasClient.validateLibraryImport(file, creativeTypeFqn);
      setPreviewData(response);
      setShowPreview(true);
      toast.update(uploadToast, {
        render: `Found ${response.summary.total_rows} items`,
        type: 'success',
        isLoading: false,
        autoClose: 2000,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to process file';
      const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.update(uploadToast, {
        render: errorResponse || message,
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCommit = async () => {
    if (!previewData) return;

    try {
      const result = await atlasClient.commitLibraryImport(previewData.preview_id);
      toast.success(`Created ${result.created_count} items`);
      if (result.skipped_count > 0) {
        toast.info(`Skipped ${result.skipped_count} rows with errors`);
      }
      setShowPreview(false);
      setPreviewData(null);
      onImportComplete();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Import failed';
      const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(errorResponse || message);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
  };

  // PDF Import Handlers
  const handlePdfUploadClick = () => {
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
      pdfInputRef.current.click();
    }
    setIsOpen(false);
  };

  const handlePdfSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !creativeTypeFqn) return;

    // Validate file type
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setIsPdfUploading(true);
    try {
      await atlasClient.startLibraryPdfImport(file, creativeTypeFqn);
      toast.success('PDF import started');
      onImportComplete();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload PDF';
      const errorResponse = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(errorResponse || message);
    } finally {
      setIsPdfUploading(false);
    }
  };

  const disabledTitle = disabled
    ? 'Select a creative type and switch to My scope to import'
    : undefined;

  return (
    <>
      <div className={boardStyles.filterControl} ref={dropdownRef}>
        <button
          className={buttonClassName || `${boardStyles.actionButton} ${boardStyles.newButtonLight}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isUploading || isPdfUploading}
          title={disabledTitle}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          Import
          <KeyboardArrowDownIcon
            style={{ fontSize: 16 }}
            className={`${boardStyles.dropdownArrow} ${isOpen ? boardStyles.open : ''}`}
          />
        </button>

        {isOpen && (
          <ul className={boardStyles.actionsDropdownMenu} role="menu" aria-label="Import options">
            <li
              className={boardStyles.actionsDropdownItem}
              role="menuitem"
              onClick={handleDownloadTemplate}
            >
              <span className={boardStyles.actionsDropdownItemLabel}>
                Download Template
              </span>
              <span className={boardStyles.actionsDropdownItemDesc}>
                Download a template for importing {creativeTypeName || 'this type'}
              </span>
            </li>
            <li
              className={boardStyles.actionsDropdownItem}
              role="menuitem"
              onClick={handleUploadClick}
            >
              <span className={boardStyles.actionsDropdownItemLabel}>
                Upload Excel
              </span>
              <span className={boardStyles.actionsDropdownItemDesc}>
                Import {creativeTypeName || 'items'} from an Excel file (max 50)
              </span>
            </li>
            <li
              className={boardStyles.actionsDropdownItem}
              role="menuitem"
              onClick={handlePdfUploadClick}
            >
              <span className={boardStyles.actionsDropdownItemLabel}>
                Upload PDF
              </span>
              <span className={boardStyles.actionsDropdownItemDesc}>
                Extract items from a PDF file
              </span>
            </li>
          </ul>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={handlePdfSelected}
      />

      {showPreview && previewData && (
        <ImportPreviewModal
          preview={previewData}
          creativeTypeName={creativeTypeName}
          onClose={handleClosePreview}
          onCommit={handleCommit}
        />
      )}
    </>
  );
};
